/**
 * PHASE 5: OfflineSyncProvider
 * Global context provider for offline sync functionality
 * 
 * Wrap your app with this provider to enable offline capabilities:
 * - Automatic database initialization
 * - Network monitoring
 * - Background sync registration
 * - Global sync state access
 */

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { 
  initOfflineDb, 
  initNetworkMonitoring, 
  subscribeToConnectivity,
  getNetworkStatus,
  getQueueStats,
  listQueueItems,
  replayPendingQueue,
  clearCompletedQueueItems
} from '@/lib/offline';

// Context
const OfflineSyncContext = createContext(null);

/**
 * Hook to access offline sync context
 */
export function useOfflineSyncContext() {
  const context = useContext(OfflineSyncContext);
  if (!context) {
    throw new Error('useOfflineSyncContext must be used within OfflineSyncProvider');
  }
  return context;
}

/**
 * OfflineSyncProvider Component
 */
export function OfflineSyncProvider({ 
  children,
  autoSync = true,
  syncIntervalMs = 30000 
}) {
  const [isOnline, setIsOnline] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [conflictCount, setConflictCount] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [queueItems, setQueueItems] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const syncIntervalRef = useRef(null);
  const initializedRef = useRef(false);

  // Refresh queue statistics
  const refreshStats = useCallback(async () => {
    try {
      const stats = await getQueueStats();
      setPendingCount(stats.pending + stats.syncing);
      setFailedCount(stats.failed);
      setConflictCount(stats.conflict);
      
      const items = await listQueueItems();
      setQueueItems(items.sort((a, b) => b.createdAt - a.createdAt));
    } catch (error) {
      console.error('[OfflineSyncProvider] Failed to refresh stats:', error);
    }
  }, []);

  // Sync now function
  const syncNow = useCallback(async () => {
    if (isSyncing || !isOnline) {
      return { success: false, reason: isSyncing ? 'already_syncing' : 'offline' };
    }
    
    setIsSyncing(true);
    
    try {
      const results = await replayPendingQueue({
        limit: 50,
        onProgress: refreshStats
      });
      
      setLastSyncedAt(Date.now());
      await refreshStats();
      
      return { success: true, results };
    } catch (error) {
      console.error('[OfflineSyncProvider] Sync error:', error);
      return { success: false, reason: 'error', error };
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, isOnline, refreshStats]);

  // Clear completed items
  const clearCompleted = useCallback(async () => {
    try {
      await clearCompletedQueueItems();
      await refreshStats();
    } catch (error) {
      console.error('[OfflineSyncProvider] Clear error:', error);
    }
  }, [refreshStats]);

  // Initialize on mount
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    const init = async () => {
      try {
        // Initialize IndexedDB
        await initOfflineDb();
        
        // Initialize network monitoring
        initNetworkMonitoring();
        
        // Get initial status
        const status = getNetworkStatus();
        setIsOnline(status.online);
        
        // Load initial stats
        await refreshStats();
        
        // Subscribe to connectivity changes
        subscribeToConnectivity((status) => {
          setIsOnline(status.online);
          
          // Auto-sync when coming back online
          if (status.online && autoSync) {
            syncNow();
          }
        });
        
        // Register service worker message handler for auth token requests
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data?.type === 'GET_AUTH_TOKEN' && event.ports[0]) {
              const token = localStorage.getItem('staff_token') || 
                           localStorage.getItem('backoffice_token') ||
                           localStorage.getItem('auth_token');
              event.ports[0].postMessage({ token });
            }
            
            // Handle sync completion from SW
            if (event.data?.type === 'SYNC_COMPLETE') {
              refreshStats();
            }
          });
        }
        
        setIsInitialized(true);
        console.log('[OfflineSyncProvider] Initialized');
        
      } catch (error) {
        console.error('[OfflineSyncProvider] Init error:', error);
        setIsInitialized(true); // Still mark as initialized to prevent blocking
      }
    };
    
    init();
  }, [autoSync, refreshStats, syncNow]);

  // Auto-sync interval
  useEffect(() => {
    if (!autoSync || !isInitialized) return;
    
    const startAutoSync = () => {
      if (syncIntervalRef.current) return;
      
      syncIntervalRef.current = setInterval(() => {
        if (document.visibilityState === 'visible' && isOnline && !isSyncing) {
          // Check if there are pending items before syncing
          getQueueStats().then(stats => {
            if (stats.pending > 0) {
              syncNow();
            }
          });
        }
      }, syncIntervalMs);
    };
    
    const stopAutoSync = () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    };
    
    // Start when tab is visible
    if (document.visibilityState === 'visible') {
      startAutoSync();
    }
    
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshStats();
        startAutoSync();
      } else {
        stopAutoSync();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stopAutoSync();
    };
  }, [autoSync, isInitialized, isOnline, isSyncing, syncIntervalMs, syncNow, refreshStats]);

  // Compute status label
  const syncStatusLabel = (() => {
    if (!isOnline) return 'Offline';
    if (isSyncing) return 'Syncing...';
    if (conflictCount > 0) return `${conflictCount} conflict${conflictCount > 1 ? 's' : ''}`;
    if (failedCount > 0) return `${failedCount} failed`;
    if (pendingCount > 0) return `${pendingCount} pending`;
    return 'Online';
  })();

  const value = {
    // State
    isOnline,
    isSyncing,
    pendingCount,
    failedCount,
    conflictCount,
    lastSyncedAt,
    queueItems,
    syncStatusLabel,
    isInitialized,
    
    // Actions
    syncNow,
    clearCompleted,
    refreshStats
  };

  return (
    <OfflineSyncContext.Provider value={value}>
      {children}
    </OfflineSyncContext.Provider>
  );
}

export default OfflineSyncProvider;
